import Vue from 'vue'
import axios from 'axios'
import moment from 'moment'

import App from './App.vue'
import vuetify from './plugins/vuetify'
import router from './router'
import store from './store'
import i18n from './i18n'
import eventBus, { showToastError } from './event-bus'
import users from './api/users'
import server from './api/server'

Vue.config.productionTip = false

axios.defaults.baseURL = process.env.VUE_APP_API_BASEURL + (process.env.VUE_APP_API_BASEURL.endsWith('/') ? '' : '/') + 'api'
axios.defaults.headers.common['Accept'] = 'application/json, application/octet-stream'
axios.defaults.timeout = Number(process.env.VUE_APP_API_TIMEOUT_SECS) * 1000

Vue.mixin({
  methods: {
    convertDate(value) {
      if (value == null) {
        return ' - '
      }
      return moment(value).local().format()
    },
    convertAuditDate(value) {
      if (value == null) {
        return ' - '
      }
      return moment(value).format('YYYY-MM-DD HH:mm:ss.SSS')
    },
    setTableItemOperationDynamicId(name, value) {
      return name + '-' + value
    }
  }
})

// refresh login
async function waitForLoggerActivation() {
  return new Promise(resolve => {
    eventBus.$once('loggerActivated', () => resolve())
  })
}
async function promptForRefreshLoginPassword(username) {
  eventBus.$emit('showRefreshLogin', { username })
  return new Promise(resolve => {
    eventBus.$once('refreshLogin', data => resolve(data.password))
  })
}

let skipInterception = false
async function getIdToken() {
  let username = store.state.currentUser.username
  const expiresInMs = new Date(store.state.currentUser.expiresAt).getTime() - Date.now()
  if (expiresInMs / 1000 > 120) {
    return store.state.currentUser.idToken
  } else {
    store.state.currentUser.idToken = null
  }

  let ok = false
  let currentUser, versionInfo
  do {
    const password = await promptForRefreshLoginPassword(username)

    try {
      skipInterception = true
      currentUser = await users.login(username, password)
      versionInfo = await server.getVersionInfo()
      skipInterception = false
      ok = true
    } catch (ignore) {
      skipInterception = false
    }
  } while (!ok)

  store.commit('setCurrentUser', Object.assign({}, currentUser, { features: versionInfo.allowedFeatures }))
  return currentUser.idToken
}

let loggerActivated = false

async function handleRequest(config) {
  if ((config.url === 'audit/logger' && config.method === 'get') || skipInterception) {
    return config
  }

  if (!loggerActivated) {
    await waitForLoggerActivation()
  }

  if (store.state.currentUser) {
    const idToken = await getIdToken()
    config.headers['Authorization'] = 'Bearer ' + idToken
  }

  return config
}

// logger activation
async function promptForLoggerActivation(loggerInfo) {
  eventBus.$emit('showActivateLogger', { loggerInfo })
  return new Promise(resolve => {
    eventBus.$once('activateLogger', data => resolve(data.password))
  })
}

async function checkLoggerActivation() {
  do {
    let resp

    try {
      resp = await axios.get('audit/logger')
    } catch (error) {
      showToastError(i18n.t('toastMessage.errorWhileRetrievingData'))
      return
    }

    let loggerInfo = resp.data

    if (loggerInfo.keyStoreActivated && !loggerInfo.failed) {
      loggerActivated = true
      eventBus.$emit('loggerActivated')
      return
    }

    const password = await promptForLoggerActivation(resp.data)

    try {
      skipInterception = true
      await axios.post('keystores/logger/activate', { password, autoActivation: false })
      loggerActivated = true
      eventBus.$emit('loggerActivated')
      return
    } catch (error) {
      showToastError(i18n.t('toastMessage.errorWhileRetrievingData'))
    } finally {
      skipInterception = false
    }
  } while (true)
}

moment.defaultFormat = 'YYYY.MM.DD, HH:mm:ss'
axios.interceptors.request.use(handleRequest)
checkLoggerActivation()

new Vue({
  vuetify,
  router,
  store,
  i18n,
  render: h => h(App)
}).$mount('#app')
